import { useState, useEffect } from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { subscribeReconnectState } from "@/lib/realtime-reconnect";
import { subscribeNetwork, getNetworkState } from "@/lib/native-network";

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!getNetworkState().connected);
  const [wsDisconnected, setWsDisconnected] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  useEffect(() => {
    // Native plugin (iOS/Android) gives accurate cellular/wifi transitions;
    // wrapper falls back to navigator online/offline events on web.
    const unsubNet = subscribeNetwork((state) => {
      setIsOffline(!state.connected);
      if (state.connected) setWsDisconnected(false);
    });

    // Only show WS banner after sustained disconnection (15+ failed attempts ≈ ~2 min)
    const unsub = subscribeReconnectState((state) => {
      const showBanner = !state.isConnected && state.failedAttempts >= 15 && !getNetworkState().connected;
      setWsDisconnected(showBanner);
      setFailedAttempts(state.failedAttempts);
    });

    return () => {
      unsubNet();
      unsub();
    };
  }, []);

  if (!isOffline && !wsDisconnected) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-2 py-2 px-4",
        "bg-destructive text-destructive-foreground text-xs font-medium",
        "animate-in slide-in-from-top duration-300"
      )}
    >
      {isOffline ? (
        <>
          <WifiOff className="h-3.5 w-3.5" />
          <span>No internet connection</span>
        </>
      ) : (
        <>
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          <span>Reconnecting… ({failedAttempts} attempts)</span>
        </>
      )}
    </div>
  );
}
